/**
 * AdminPromoPage – admin-only UI to create up to 10 promo codes.
 *
 * Two code types:
 *   - "Full access" (100% free lifetime) — these are the free promo codes.
 *   - "% off at checkout" — the discount gets applied when the user picks a plan.
 *
 * Hard cap of 10 codes total is enforced by the backend.
 */
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CaretLeft, Ticket, Plus, Copy, Trash, SpinnerGap, Crown } from '@phosphor-icons/react';
import { http, apiErrorMessage } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';

export default function AdminPromoPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [codes, setCodes] = useState([]);
  const [kind, setKind] = useState('full');
  const [percent, setPercent] = useState(50);
  const [maxRedemptions, setMaxRedemptions] = useState(1);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (user && user.role !== 'admin') { navigate('/home', { replace: true }); }
  }, [user, navigate]);

  async function load() {
    try {
      const { data } = await http.get('/admin/promo');
      setCodes(data || []);
    } catch (e) {
      setError(apiErrorMessage(e));
    }
  }
  useEffect(() => { load(); }, []);

  async function create() {
    setBusy(true);
    setError('');
    try {
      await http.post('/admin/promo', {
        kind,
        percent_off: kind === 'percent' ? percent : 100,
        max_redemptions: maxRedemptions,
        note,
      });
      setNote('');
      await load();
    } catch (e) {
      setError(apiErrorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  async function remove(code) {
    if (!window.confirm(`Delete ${code}?`)) return;
    try {
      await http.delete(`/admin/promo/${encodeURIComponent(code)}`);
      setCodes((arr) => arr.filter((c) => c.code !== code));
    } catch (e) {
      setError(apiErrorMessage(e));
    }
  }

  function copy(code) { navigator.clipboard?.writeText(code).catch(() => {}); }

  const atCap = codes.length >= 10;
  const used = codes.reduce((s, c) => s + (c.redemptions_used || 0), 0);

  return (
    <div className="lf-shell px-6 py-8" data-testid="admin-promo-page">
      <div className="flex items-center gap-2">
        <Link to="/home" className="grid h-10 w-10 place-items-center rounded-full border border-[var(--lf-border)] bg-[var(--lf-surface)]" data-testid="admin-back">
          <CaretLeft size={18} />
        </Link>
        <div>
          <div className="text-xs uppercase tracking-widest text-[var(--lf-text-muted)]">Admin</div>
          <h1 className="text-2xl font-bold">Promo codes</h1>
        </div>
      </div>

      <div className="lf-card mt-5 p-5">
        <div className="flex items-center justify-between">
          <div className="text-sm text-[var(--lf-text-muted)]">Used / capacity</div>
          <div className="font-bold">{codes.length} / 10 codes · {used} redemptions</div>
        </div>
        <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-[var(--lf-surface-muted)]">
          <div className="h-full bg-[var(--lf-primary)]" style={{ width: `${codes.length * 10}%` }} />
        </div>
      </div>

      {/* Create */}
      <div className="lf-card mt-5 p-5" data-testid="admin-create-card">
        <div className="flex items-center gap-2 text-sm font-semibold">
          <Ticket size={16} weight="duotone" className="text-[var(--lf-primary)]" />
          Create new code
        </div>

        <div className="mt-3 grid grid-cols-2 gap-2">
          <button onClick={() => setKind('full')} className={`rounded-xl p-3 text-left transition-all ${kind === 'full' ? 'bg-[var(--lf-primary)] text-white' : 'bg-[var(--lf-surface-muted)]'}`} data-testid="kind-full">
            <div className="font-bold text-sm">Free lifetime</div>
            <div className="text-xs opacity-80">100% off forever</div>
          </button>
          <button onClick={() => setKind('percent')} className={`rounded-xl p-3 text-left transition-all ${kind === 'percent' ? 'bg-[var(--lf-primary)] text-white' : 'bg-[var(--lf-surface-muted)]'}`} data-testid="kind-percent">
            <div className="font-bold text-sm">% discount</div>
            <div className="text-xs opacity-80">Applied at checkout</div>
          </button>
        </div>

        {kind === 'percent' ? (
          <div className="mt-3">
            <label htmlFor="admin-percent" className="text-xs font-semibold text-[var(--lf-text-muted)]">Discount %</label>
            <input id="admin-percent" type="number" min={1} max={100} value={percent} onChange={(e) => setPercent(Number(e.target.value))} className="lf-input mt-1" data-testid="percent-input" />
          </div>
        ) : null}

        <div className="mt-3">
          <label htmlFor="admin-max-redemptions" className="text-xs font-semibold text-[var(--lf-text-muted)]">Max redemptions</label>
          <input id="admin-max-redemptions" type="number" min={1} max={100} value={maxRedemptions} onChange={(e) => setMaxRedemptions(Number(e.target.value))} className="lf-input mt-1" data-testid="max-input" />
        </div>
        <div className="mt-3">
          <label htmlFor="admin-note" className="text-xs font-semibold text-[var(--lf-text-muted)]">Note (optional)</label>
          <input id="admin-note" value={note} onChange={(e) => setNote(e.target.value)} className="lf-input mt-1" placeholder="e.g. Beta testers" data-testid="note-input" />
        </div>

        <button
          onClick={create}
          disabled={busy || atCap}
          className="lf-btn lf-btn-primary mt-4 w-full"
          data-testid="admin-create-btn"
        >
          {busy ? <SpinnerGap size={16} className="animate-spin" /> : <Plus size={16} weight="bold" />}
          {atCap ? 'Cap reached' : 'Create code'}
        </button>
        {error ? <div className="mt-2 text-sm text-[var(--lf-destructive)]">{error}</div> : null}
      </div>

      {/* List */}
      <div className="mt-6 space-y-2">
        {codes.map((c) => (
          <div key={c.code} className="lf-card p-4 flex items-start gap-3" data-testid={`admin-code-${c.code}`}>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <span className="font-mono text-sm font-bold tracking-wider">{c.code}</span>
                {c.kind === 'full' ? (
                  <span className="lf-chip lf-chip-primary"><Crown size={12} weight="fill" />Free</span>
                ) : (
                  <span className="lf-chip lf-chip-accent">{c.percent_off}% off</span>
                )}
              </div>
              <div className="mt-1 text-xs text-[var(--lf-text-muted)]">
                {c.redemptions_used}/{c.max_redemptions} redeemed
                {c.note ? ` · ${c.note}` : ''}
              </div>
            </div>
            <button onClick={() => copy(c.code)} className="grid h-8 w-8 place-items-center rounded-full bg-[var(--lf-surface-muted)]" data-testid={`copy-${c.code}`}>
              <Copy size={14} />
            </button>
            <button onClick={() => remove(c.code)} className="grid h-8 w-8 place-items-center rounded-full bg-[var(--lf-surface-muted)] text-[var(--lf-destructive)]" data-testid={`delete-${c.code}`}>
              <Trash size={14} />
            </button>
          </div>
        ))}
      </div>
      <div className="h-10" />
    </div>
  );
}
